/* eslint-disable @typescript-eslint/no-explicit-any */
import { Check, Close } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { DataGridCell, currencyCell } from 'components/cells';
import equipmentStatusCell from 'components/cells/equipment-status-cell';
import listCell from 'components/cells/list-cell';
import {
  filterOperators,
  booleanFilterOperators,
  numberFilterOperators,
  filterListOperators,
  dateFilterOperators
} from 'components/enhanced';
import { booleanValueFormatter } from 'components/enhanced/data-grid/utils/helpers';
import ConfirmationUnlinkTableDialog from 'components/ui/feedback/ConfirmationGridUnlink';
import FileUploader from 'components/ui/file-uploader/FileUploader';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { equipmentService } from 'shared/services';

const equipmentOptions = ['NEW', 'REFURBISHED', 'DECOMMISSIONED'];

export const EquipmentColumns = (
  ivdr_classes: string[],
  onUploadSuccess?: any,
  detailNav = false
): GridColDef[] => {
  const translationPrefix = 'entities.equipment';
  const { t } = useTranslation();
  const isSuperAdmin = IsSuperAdminGuard();

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: t(`${translationPrefix}.code`),
      width: 120,
      filterOperators,
      renderCell: (params: GridRenderCellParams) => {
        const workstation = params.row;
        return detailNav ? (
          <div>
            <Link to={`${workstation.id}`}>
              <b className="text-secondary-500">{workstation.code}</b>
            </Link>
          </div>
        ) : (
          <DataGridCell content={params.value as string} />
        );
      }
    },
    ...(isSuperAdmin
      ? [
          {
            field: 'association',
            headerName: t(`global.association`),
            width: 80,
            sortable: false,
            filterOperators: booleanFilterOperators,
            renderCell: (params: GridRenderCellParams) => (
              <div>
                <Chip
                  sx={{ background: 'transparent' }}
                  icon={params.value ? <Check color="success" /> : <Close color="error" />}
                />
              </div>
            )
          }
        ]
      : []),
    {
      field: 'description',
      filterOperators,
      width: 250,
      headerName: t(`${translationPrefix}.description`)
    },
    {
      field: 'status',
      type: 'singleSelect',
      valueOptions: equipmentOptions,
      headerName: t(`${translationPrefix}.status`),
      width: 100,
      renderCell: equipmentStatusCell(t)
    },
    {
      field: 'serial_number',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.serial_number`)
    },
    {
      field: 'internal_id',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.internal_id`)
    },
    {
      field: 'transport_document_ref',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.transport_document_ref`)
    },
    {
      field: 'supplier',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.supplier`)
    },
    {
      field: 'warehouse_code',
      filterOperators,
      width: 150,
      headerName: t(`global.warehouse`)
    },
    {
      field: 'position',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.position`)
    },
    {
      field: 'work_island',
      filterOperators,
      width: 180,
      headerName: t(`${translationPrefix}.isola_di_lavoro`)
    },
    {
      field: 'instrument_type',
      filterOperators,
      width: 180,
      headerName: t(`${translationPrefix}.tipologia_strumento`)
    },
    {
      field: 'market_value',
      filterOperators: numberFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.market_value`),
      renderCell: currencyCell
    },
    {
      field: 'manufacturer',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.manufacturer`)
    },
    {
      field: 'yearly_lease_amount',
      headerName: t(`entities.contract-equipment.yearly_lease_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'yearly_maintenance_amount',
      headerName: t(`entities.contract-equipment.yearly_maintenance_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'purchase_amount',
      headerName: t(`entities.contract-equipment.purchase_amount`),
      width: 240,
      filterOperators: numberFilterOperators,
      renderCell: currencyCell
    },
    {
      field: 'accessories',
      filterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.accessories`)
    },
    {
      field: 'contracts_code',
      sortable: false,
      filterOperators: filterListOperators,
      width: 200,
      renderCell: listCell,
      headerName: t(`global.contracts`)
    },
    {
      field: 'delivery_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.delivery_date`)
    },
    {
      field: 'installation_date',
      width: 150,
      filterOperators: dateFilterOperators,
      headerName: t(`${translationPrefix}.installation_date`)
    },
    {
      field: 'testing_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.testing_date`)
    },
    {
      field: 'buying_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.buying_date`)
    },
    {
      field: 'decommission_date',
      filterOperators: dateFilterOperators,
      width: 150,
      headerName: t(`${translationPrefix}.decommission_date`)
    },
    {
      field: 'ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.ticketing_email`)
    },
    {
      field: 'cc_ticketing_email',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.cc_ticketing_email`)
    },
    {
      field: 'assistance_contact_number',
      filterOperators,
      filterable: false,
      sortable: false,
      width: 200,
      headerName: t(`${translationPrefix}.assistance_contact_number`)
    }
  ];

  [
    'technical_file',
    'user_manual',
    'ce_ivd',
    'maintenance_checklist_template',
    'preemptive_maintenance_calendar'
  ].forEach((file) => {
    columns.push({
      field: file,
      headerName: t(`${translationPrefix}.${file}`),
      valueFormatter: booleanValueFormatter,
      renderCell: (params: GridRenderCellParams) => {
        const equipment = params.row;
        return (
          <IsTenantAdministrator>
            <div>
              <FileUploader
                onUploadSuccess={onUploadSuccess}
                service={equipmentService}
                entityId={equipment.id}
                attachmentName={file}
                filename={equipment[file]}
              />
            </div>
          </IsTenantAdministrator>
        );
      },
      width: 70,
      filterable: false,
      sortable: false
    });
  });

  columns.push({
    field: 'actions',
    type: 'actions',
    flex: 0,
    minWidth: 80,
    width: 80,
    headerName: t(`global.actions`),
    getActions: (params: GridRowParams) => {
      const actions = [];
      actions.push(
        <IsTenantAdministrator key={123}>
          <ConfirmationUnlinkTableDialog
            id={params.row.id}
            handleUnlink={(id) => {
              equipmentService.purgeLinks(id).then(() => {
                if (onUploadSuccess) onUploadSuccess();
              });
            }}
          />
        </IsTenantAdministrator>
      );
      return actions;
    }
  } as GridColDef);

  // Nascondi completamente la colonna "Associato" per non-superadmin (mai mostrarla neanche vuota)
  return (columns as GridColDef[]).filter((col) => col.field !== 'association' || isSuperAdmin);
};
